import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { getAllVHNDData, getVillages } from '../services/authService';
import Loader from '../components/Loader';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const VHNDManagement = () => {
  const navigate = useNavigate();
  const [vhndData, setVhndData] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({
    Child: 0,
    'Pregnant Woman': 0,
    Elderly: 0,
    'N/A': 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [villages, setVillages] = useState([]);
  const [filters, setFilters] = useState({
    villageId: '',
    location: '',
    duration: 'all',
    status: 'all',
  });
  const [searchTimeout, setSearchTimeout] = useState(null);
  const limit = 10;
  const selectedVillageId = useSelector((state) => state.village.selectedVillageId);
  const ashaId = localStorage.getItem('asha_id');

  useEffect(() => {
    const fetchVhndData = async () => {
      setLoading(true);
      try {
        const queryParams = {
          page: currentPage,
          limit,
          villageId: selectedVillageId || filters.villageId,
          location: filters.location,
          duration: filters.duration,
          status: filters.status,
        };
        const response = await getAllVHNDData(queryParams, ashaId, selectedVillageId || filters.villageId);
        setVhndData(Array.isArray(response.data.data) ? response.data.data : []);
        setCategoryCounts(response.data.categoryCounts || { Child: 0, 'Pregnant Woman': 0, Elderly: 0, 'N/A': 0 });
        setTotalPages(response.data.pagination.totalPages || 1);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    const fetchVillages = async () => {
      try {
        const response = await getVillages(ashaId);
        const villageList = response.data || [];
        setVillages(villageList);
      } catch (error) {
        console.error('Error fetching villages:', error);
      }
    };

    if (ashaId) {
      fetchVhndData();
      fetchVillages();
    }
  }, [currentPage, filters, selectedVillageId, ashaId]);

  useEffect(() => {
    return () => {
      if (searchTimeout) clearTimeout(searchTimeout);
    };
  }, [searchTimeout]);

  const handleVHNDClick = () => {
    navigate('/VHND-create');
  };

  const handleVHNDupcoming = () => {
    navigate('/vhnd-upcoming');
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'location') {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      const timeout = setTimeout(() => {
        if (value.trim() && value.length >= 3) {
          setFilters((prev) => ({
            ...prev,
            [name]: value,
          }));
          setCurrentPage(1);
        } else if (!value.trim()) {
          setFilters((prev) => ({
            ...prev,
            [name]: '',
          }));
          setCurrentPage(1);
        }
      }, 500);
      setSearchTimeout(timeout);
    } else {
      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
      setCurrentPage(1);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const renderPagination = () => {
    const buttons = [];
    const maxButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    buttons.push(
      <div
        key="prev"
        className={`pagination-button ${currentPage === 1 ? 'disabled' : ''}`}
        onClick={() => handlePageChange(currentPage - 1)}
      >
        <span className="material-icons">chevron_left</span>
      </div>
    );

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <div
          key={i}
          className={`pagination-button ${i === currentPage ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </div>
      );
    }

    buttons.push(
      <div
        key="next"
        className={`pagination-button ${currentPage === totalPages ? 'disabled' : ''}`}
        onClick={() => handlePageChange(currentPage + 1)}
      >
        <span className="material-icons">chevron_right</span>
      </div>
    );

    return buttons;
  };

  // Chart data for category counts
  const chartData = {
    labels: ['Child', 'Pregnant Woman', 'Elderly', 'N/A'],
    datasets: [
      {
        label: 'Beneficiary Categories',
        data: [
          categoryCounts.Child,
          categoryCounts['Pregnant Woman'],
          categoryCounts.Elderly,
          categoryCounts['N/A'],
        ],
        backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56', '#D3D3D3'],
        borderColor: ['#2C83C4', '#D94F70', '#D4A017', '#A9A9A9'],
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: { color: '#333' },
      },
      title: {
        display: true,
        text: 'Beneficiary Distribution by Category',
        color: '#333',
        font: { size: 16 },
      },
    },
  };

  if (loading) return <Loader />;
  if (error) return <div className="error">{error}</div>;

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">VHND Management</div>
          <div className="page-subtitle">
            View and manage past Village Health and Nutrition Day events
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="button outline" onClick={handleVHNDupcoming}>
            <span className="material-icons icon">calendar_today</span>
            Upcoming Events
          </button>
          <button className="button" onClick={handleVHNDClick}>
            <span className="material-icons icon">add</span>
            Schedule New VHND
          </button>
        </div>
      </div>

  
      <div className="filters-bar">
        <div className="filter-input">
          <span className="material-icons icon">search</span>
          <input
            type="text"
            name="location"
            placeholder="Search for VHND..."
            value={console.log(filters.location)}
            onChange={handleFilterChange}
          />
        </div>
        <div className="filter-select">
          <span className="material-icons icon">location_on</span>
          <select name="villageId" value={filters.villageId} onChange={handleFilterChange}>
            {villages.length > 0 ? (
              <>
                <option value="">All Villages</option>
                {villages.map((village) => (
                  <option key={village.village_id} value={village.village_id}>
                    {village.village_name}
                  </option>
                ))}
              </>
            ) : (
              <option>No villages available</option>
            )}
          </select>
        </div>
        <div className="filter-select">
          <span className="material-icons icon">event</span>
          <select name="duration" value={filters.duration} onChange={handleFilterChange}>
            <option value="all">All Time</option>
            <option value="month">Past Month</option>
            <option value="quarter">Past Quarter</option>
            <option value="year">Past Year</option>
          </select>
        </div>
        <div className="filter-select">
          <span className="material-icons icon">checklist</span>
          <select name="status" value={filters.status} onChange={handleFilterChange}>
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="vhnd-grid">
        {vhndData.length === 0 ? (
          <div>No VHND data found</div>
        ) : (
          vhndData.map((vhnd) => {
            // Calculate per-VHND category counts
            const vhndCategoryCounts = {
              Child: 0,
              'Pregnant Woman': 0,
              Elderly: 0,
              'N/A': 0,
            };
            vhnd.VhndParticipations.forEach((participation) => {
              const category = participation.Beneficiary?.category || 'N/A';
              vhndCategoryCounts[category]++;
            });

            return (
              <div className="vhnd-card" key={vhnd.vhnd_id}>
                <div className="vhnd-header" style={{ backgroundColor: '#4CAF50' }}>
                  <div className="vhnd-date">{formatDate(vhnd.vhnd_date)}</div>
                  <div className="vhnd-village">{vhnd.location}</div>
                  <div className="vhnd-status completed">
                    {vhnd.status || (vhnd.end_time ? 'Completed' : 'Scheduled')}
                  </div>
                </div>
                <div className="vhnd-content">
                  <div className="vhnd-stats">
                    <div className="vhnd-stat">
                      <div className="vhnd-stat-value" style={{ color: '#4CAF50' }}>
                        {vhnd.attendance || vhnd.expected_beneficiaries || 'N/A'}
                      </div>
                      <div className="vhnd-stat-label">Attendees</div>
                    </div>
                    <div className="vhnd-stat">
                      <div className="vhnd-stat-value" style={{ color: '#4CAF50' }}>
                        {vhndCategoryCounts.Child}
                      </div>
                      <div className="vhnd-stat-label">Children</div>
                    </div>
                    <div className="vhnd-stat">
                      <div className="vhnd-stat-value" style={{ color: '#4CAF50' }}>
                        {vhndCategoryCounts['Pregnant Woman']}
                      </div>
                      <div className="vhnd-stat-label">Pregnant</div>
                    </div>
                    <div className="vhnd-stat">
                      <div className="vhnd-stat-value" style={{ color: '#4CAF50' }}>
                        {vhndCategoryCounts.Elderly}
                      </div>
                      <div className="vhnd-stat-label">Elderly</div>
                    </div>
                  </div>

                  <div className="vhnd-services">
                    <div className="vhnd-services-title">Services Provided</div>
                    <div className="vhnd-services-list">
                      {vhnd.services_provided?.split(',').map((service, idx) => (
                        <div className="vhnd-service" key={idx}>
                          {service.trim()}
                        </div>
                      )) || <div>No services provided</div>}
                      {vhnd.VhndParticipations?.map((participation) =>
                        participation.VhndParticipantServices?.map((service, idx) => (
                          <div className="vhnd-service" key={idx}>
                            {service.service_details}
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="vhnd-officials">
                    <div className="vhnd-officials-title">Officials Present</div>
                    <div className="vhnd-officials-list">
                      {vhnd.VhndStaffAttendances?.map((staff, idx) => (
                        <div key={idx}>
                          {staff.official_name} ({staff.official_role})
                        </div>
                      )) || 'N/A'}
                    </div>
                  </div>

                  <div className="vhnd-actions">
                    <div className="vhnd-action" style={{ color: '#4CAF50' }}>
                      <span className="material-icons icon">visibility</span>
                      View Details
                    </div>
                    <div className="vhnd-action" style={{ color: '#4CAF50' }}>
                      <span className="material-icons icon">file_download</span>
                      Report
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="pagination">{renderPagination()}</div>
    </>
  );
};

export default VHNDManagement;  